import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigation, useRoute } from '@react-navigation/native'
import { InvoiceDetails } from './InvoiceDetails'
import CompanyInvoices from './CompanyInvoices'
import { InvoicePaymentType, routes } from '@linqpal/models'
import Loading from '../../../Loading'
import {
  invoiceStatus,
  PROCESSING_STATUSES,
} from '@linqpal/models/src/dictionaries'
import CompanyInactiveInvoices from './CompanyInactiveInvoices'
import RootStore from '../../../../store'
import { observer } from 'mobx-react'
import { PaymentHistoryInvoiceReceipt } from '../Receipt/PaymentHistoryInvoiceReceipt'

const Screen = {
  InvoiceDetails,
  CompanyInvoices,
  CompanyInactiveInvoices,
  PaymentHistoryInvoiceReceipt,
}

export const payableInvoiceStatuses = [
  invoiceStatus.pastDue,
  invoiceStatus.due,
  invoiceStatus.placed,
  invoiceStatus.paymentFailed,
  invoiceStatus.applicationCancelled,
  invoiceStatus.applicationRejected,
]

export const isActiveInvoice = (invoice) => {
  const processing = invoice.loanApp
    ? PROCESSING_STATUSES.includes(invoice.loanApp?.status)
    : false
  return (
    ['invoice', 'sales_order'].includes(invoice.type) &&
    invoice.company &&
    payableInvoiceStatuses.includes(invoice.status) &&
    !processing &&
    invoice.totalRemainingAmount
  )
}

export default observer(function ScreenSelect() {
  const route: any = useRoute()
  const navigation: any = useNavigation()
  const [invoices, setInvoices] = useState<any[]>([])
  const [customer, setCustomer] = useState<any[]>([])
  const [isFactoring, setIsFactoring] = useState<boolean | null>(null)
  const [currentInvoiceInactive, setCurrentInvoiceInactive] =
    useState<boolean>(false)
  const [currentInvoiceFactoring, setCurrentInvoiceFactoring] =
    useState<boolean>(false)
  const [screen, setScreen] = useState<string | null>(null)
  const {
    routeParams: { action },
  } = RootStore

  const loadInvoices = useCallback(() => {
    routes.invoices
      .companyInvoices(
        route.params?.id,
        route.params?.companyId,
        route.params?.invoicesIds,
      )
      .then((r) => {
        const activeInvoices: any[] = []
        if (route.params?.id) {
          r.invoices.forEach((invoice) => {
            if (isActiveInvoice(invoice)) {
              activeInvoices.push({ ...invoice, selected: true })
            } else if (route.params?.id === invoice._id) {
              if (invoice.paymentType === InvoicePaymentType.FACTORING) {
                setCurrentInvoiceFactoring(true)
              } else {
                setCurrentInvoiceInactive(true)
              }
            }
          })
        } else {
          r.invoices.forEach((invoice) => {
            if (
              ['invoice', 'sales_order'].includes(invoice.type) &&
              invoice.company
            ) {
              activeInvoices.push({ ...invoice, selected: true })
            }
          })
        }
        if (activeInvoices.length > 0) {
          setInvoices(activeInvoices)
          setCustomer({ ...r.customer, invoiceId: activeInvoices[0]?._id })
        } else {
          if (r.invoices.length > 0) {
            if (route.params?.id) {
              const invoice = r.invoices.find(
                (i) => i._id.toString() === route.params?.id,
              )
              if (invoice) {
                setInvoices([invoice])
              } else {
                setInvoices([r.invoices[0]])
              }
            } else {
              setInvoices(r.invoices)
            }
          }
        }
      })
  }, [setInvoices, setCustomer, route.params])

  useEffect(() => {
    if (action === 'reloadInvoices') {
      loadInvoices()
      RootStore.initRouteParams({})
    }
  }, [action, loadInvoices])

  useEffect(() => {
    loadInvoices()
  }, [route.params, loadInvoices])

  useEffect(() => {
    const fetchData = async () => {
      if (route.params?.companyId || route.params?.account) {
        try {
          let response: any
          if (route.params?.companyId) {
            response = await routes.company.isFactoringEnabled({
              supplierId: route.params?.companyId,
            })
          } else {
            response = await routes.company.isFactoringEnabledForCustomer({
              customerAccountId: route.params?.account,
            })
          }

          setIsFactoring(
            response.arAdvanceIsEnabled && response.inHouseCreditIsEnabled,
          )
        } catch (error) {
          console.error('Error fetching data:', error)
        }
      } else if (route.params?.invoicesIds && route.params?.isInvited) {
        setIsFactoring(false)
      }
    }

    fetchData()
  }, [route.params?.companyId, route.params?.account, route.params?.isInvited, route.params?.invoicesIds])

  useEffect(() => {
    if (typeof isFactoring === 'boolean' || !route.params?.companyId) {
      const totalToPay = invoices?.reduce(
        (sum, i) => (!isActiveInvoice(i) ? sum : sum + i.totalRemainingAmount),
        0,
      )
      if (isFactoring && route.params?.account) {
        // for ARA invoices that paid using payment link
        setScreen('PaymentHistoryInvoiceReceipt')
      } else {
        if (
          isFactoring ||
          (route.params?.invoicesIds && !route.params?.isInvited)
        ) {
          setScreen('CompanyInactiveInvoices')
        } else {
          if (currentInvoiceFactoring) {
            setScreen('PaymentHistoryInvoiceReceipt')
          } else if (currentInvoiceInactive) {
            setScreen('InvoiceDetails')
          } else if (invoices.length > 0 && totalToPay === 0) {
            setScreen('CompanyInactiveInvoices')
          } else if (invoices.length > 1) {
            setScreen('CompanyInvoices')
          } else {
            setScreen('InvoiceDetails')
          }
        }
      }
    }
  }, [invoices, navigation, currentInvoiceInactive, currentInvoiceFactoring, isFactoring, route.params?.companyId, route.params?.invoicesIds, route.params.account, route.params?.isInvited])

  return useMemo(() => {
    if (!screen) return <Loading />
    const Component = Screen[screen]

    const updatedRoute =
      screen === 'InvoiceDetails' &&
      !currentInvoiceInactive &&
      invoices.length === 1
        ? { ...route, params: { ...route.params, id: invoices[0]._id } }
        : route
    return (
      <Component
        navigation={navigation}
        route={updatedRoute}
        companyInvoices={invoices}
        customer={customer}
      />
    )
  }, [customer, invoices, navigation, route, screen, currentInvoiceInactive])
})
